(() => {
  if (customElements.get('macfox-scroll-video')) return;
  const clamp = value => Math.max(0, Math.min(1, value));
  const ease = value => { const p = clamp(value); return p * p * (3 - 2 * p); };
  class MacfoxScrollVideo extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.stage = this.querySelector('.msv-stage');
      this.slices = [...this.querySelectorAll('.msv-slice')];
      this.copy = this.querySelector('.msv-copy');
      this.first = this.querySelector('.msv-title-first');
      this.second = this.querySelector('.msv-title-second');
      this.light = this.querySelector('.msv-title').cloneNode(true);
      this.light.classList.add('msv-title-light');
      this.light.setAttribute('aria-hidden', 'true');
      this.copy.append(this.light);
      this.controls = this.querySelector('.msv-controls');
      this.toggle = this.querySelector('.msv-toggle');
      this.sound = this.querySelector('.msv-sound');
      this.toggleText = this.toggle.querySelector('.msv-control-text');
      this.soundText = this.sound.querySelector('.msv-control-text');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.video.muted = true;
      this.video.controls = false;
      this.expanded = false;
      this.userPaused = false;
      this.wanted = false;
      this.playPending = false;
      this.active = true;
      this.loaded = false;
      this.frameReady = false;
      this.mediaVersion = 0;
      this.sourceMode = null;
      this.classList.add('is-enhanced');
      this.onScroll = () => this.schedule();
      window.addEventListener('scroll', this.onScroll, { passive: true, ...options });
      window.addEventListener('resize', () => this.measure(), options);
      window.visualViewport?.addEventListener('resize', () => this.schedule(), options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.video.pause();
        this.schedule();
      }, options);
      this.motion.addEventListener('change', () => this.measure(), options);
      this.video.addEventListener('loadeddata', () => {
        if (!this.frameReady && this.video.currentTime < 0.1) this.captureFrame();
        this.schedule();
      }, options);
      this.video.addEventListener('seeked', () => {
        if (!this.frameReady && this.video.currentTime < 0.1) this.captureFrame();
      }, options);
      this.video.addEventListener('play', () => this.updateControls(), options);
      this.video.addEventListener('pause', () => this.updateControls(), options);
      this.video.addEventListener('volumechange', () => this.updateControls(), options);
      this.video.addEventListener('error', () => {
        this.userPaused = true;
        this.video.pause();
        this.updateControls();
      }, options);
      this.toggle.addEventListener('click', () => {
        if (this.video.paused) {
          this.userPaused = false;
          this.load();
          this.play(true);
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }, options);
      this.sound.addEventListener('click', () => {
        this.video.muted = !this.video.muted;
        this.updateControls();
      }, options);
      this.resizeObserver = new ResizeObserver(() => {
        if (this.clientWidth !== this.width) this.measure();
      });
      this.resizeObserver.observe(this);
      this.observer = new IntersectionObserver(entries => {
        this.active = entries[0].isIntersecting;
        if (this.active) this.load();
        else { this.wanted = false; this.video.pause(); }
        this.schedule();
      }, { rootMargin: '700px 0px' });
      this.observer.observe(this);
      this.measure();
      this.updateControls();
      document.fonts?.ready.then(() => { if (this.isConnected) this.fitTitles(); });
    }

    load() {
      if (this.loaded) return;
      this.loaded = true;
      this.video.preload = 'auto';
      this.video.load();
    }

    captureFrame() {
      if (!this.video.videoWidth || this.frameReady) return;
      this.frameReady = true;
      const version = this.mediaVersion;
      try {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 1920 / this.video.videoWidth);
        canvas.width = Math.round(this.video.videoWidth * scale);
        canvas.height = Math.round(this.video.videoHeight * scale);
        canvas.getContext('2d').drawImage(this.video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
          if (!blob || !this.isConnected || version !== this.mediaVersion) return;
          this.frameURL = URL.createObjectURL(blob);
          this.video.poster = this.frameURL;
          this.slices.forEach(slice => {
            let image = slice.querySelector('.msv-frame');
            if (image.tagName !== 'IMG') {
              const replacement = document.createElement('img');
              replacement.className = 'msv-frame';
              replacement.alt = '';
              image.replaceWith(replacement);
              image = replacement;
            }
            image.removeAttribute('srcset');
            image.src = this.frameURL;
          });
          this.schedule();
        }, 'image/jpeg', 0.92);
      } catch (_) {
        this.dataset.frameFallback = 'preview-image';
      }
    }

    measure() {
      this.width = this.clientWidth;
      this.mobile = this.width < 750;
      this.selectSource();
      const nextHeight = window.innerHeight;
      const widthChanged = this.lastWidth !== this.width;
      if (!this.height || widthChanged || !this.mobile || Math.abs(nextHeight - this.height) > 160) this.height = nextHeight;
      this.lastWidth = this.width;
      this.height = Math.max(360, this.height);
      this.travel = this.height * (this.mobile ? 0.95 : 1.25);
      this.style.setProperty('--msv-height', `${this.height}px`);
      this.style.height = this.motion.matches ? 'auto' : `${this.height + this.travel + this.height * 0.65}px`;
      this.classList.toggle('is-reduced', this.motion.matches);
      if (this.motion.matches) this.video.pause();
      this.userPaused = false;
      this.fitTitles();
      this.schedule();
    }

    selectSource() {
      if (!this.dataset.mobileSrc) return;
      const mode = this.mobile ? 'mobile' : 'desktop';
      if (mode === this.sourceMode) return;
      this.sourceMode = mode;
      this.mediaVersion += 1;
      this.video.pause();
      this.frameReady = false;
      if (this.frameURL) URL.revokeObjectURL(this.frameURL);
      this.frameURL = null;
      const poster = this.dataset[`${mode}Poster`];
      this.video.poster = poster || '';
      this.slices.forEach(slice => {
        const image = slice.querySelector('img');
        if (image && poster) { image.removeAttribute('srcset'); image.src = poster; }
      });
      this.video.src = this.dataset[`${mode}Src`];
      // Keep lazy loading until the section approaches the viewport.
      if (this.loaded) this.video.load();
    }

    fitTitles() {
      [this.first, this.second].filter(Boolean).forEach(title => {
        title.style.fontSize = '';
        const initial = parseFloat(getComputedStyle(title).fontSize);
        const available = this.width * 0.90;
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        context.font = `800 ${initial}px MacfoxScrollInter, Arial, sans-serif`;
        const measured = context.measureText(title.textContent).width;
        if (measured > available) title.style.fontSize = `${Math.max(this.mobile ? 26 : 40, initial * available / measured)}px`;
        const twin = this.light.querySelector(`.${title.className}`);
        if (twin) twin.style.fontSize = title.style.fontSize;
      });
    }

    schedule() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => { this.raf = 0; this.render(); });
    }

    render() {
      if (this.clientWidth !== this.width || Math.abs(window.innerHeight - this.height) > 160) this.measure();
      const bounds = this.getBoundingClientRect();
      const visible = bounds.top < window.innerHeight && bounds.bottom > 0;
      if (this.motion.matches) {
        this.expanded = true;
        this.classList.add('is-expanded');
        this.controls.hidden = false;
        this.wanted = false;
        this.updateControlsPosition();
        if (!visible || document.hidden) this.video.pause();
        return;
      }
      const progress = clamp(-bounds.top / this.travel);
      const q = ease(progress);
      const w = this.width;
      const h = this.height;
      const startW = w * (this.mobile ? 0.265 : 0.153);
      const startH = h * (this.mobile ? 0.32 : 0.50);
      const startsX = this.mobile ? [0.05, 0.3675, 0.685] : [0.423, 0.605, 0.786];
      const startsY = this.mobile ? [0.47, 0.415, 0.495] : [0.245, 0.14, 0.295];
      const sliceW = startW + (w / 3 - startW) * q;
      const sliceH = startH + (h - startH) * q;
      let clip = '';
      this.slices.forEach((slice, index) => {
        const x = startsX[index] * w * (1 - q) + index * w / 3 * q;
        const y = startsY[index] * h * (1 - q);
        slice.style.width = `${sliceW + 0.5}px`;
        slice.style.height = `${sliceH}px`;
        slice.style.transform = `translate3d(${x}px,${y}px,0)`;
        const frame = slice.querySelector('.msv-frame');
        frame.style.width = `${sliceW * 3}px`;
        frame.style.height = `${sliceH}px`;
        frame.style.left = `${-index * sliceW}px`;
        clip += `M${x},${y}h${sliceW + 0.5}v${sliceH}h${-sliceW - 0.5}Z `;
      });
      this.light.style.clipPath = `path('${clip}')`;
      this.copy.style.opacity = 1 - ease((progress - 0.28) / 0.46);
      if (this.first) this.first.style.transform = `translate3d(${-progress * w * 0.12}px,${-progress * h * 0.10}px,0)`;
      if (this.second) this.second.style.transform = `translate3d(${progress * w * 0.12}px,${progress * h * 0.08}px,0)`;
      if (this.first) this.light.querySelector('.msv-title-first').style.transform = this.first.style.transform;
      if (this.second) this.light.querySelector('.msv-title-second').style.transform = this.second.style.transform;
      const joined = progress >= 0.998;
      if (!joined && this.expanded) {
        this.video.pause();
        try { this.video.currentTime = 0; } catch (_) {}
        this.userPaused = false;
        this.video.muted = true;
      }
      this.expanded = joined;
      this.classList.toggle('is-expanded', joined);
      const stageBounds = this.stage.getBoundingClientRect();
      this.updateControlsPosition(stageBounds);
      const fullyVisible = joined && stageBounds.top > -h * 0.35 && stageBounds.bottom > h * 0.65;
      this.controls.hidden = !joined;
      this.wanted = fullyVisible && !document.hidden && !this.userPaused;
      if (this.wanted && this.active) { this.load(); this.play(); }
      else if (!this.video.paused) this.video.pause();
      this.updateControls();
    }

    play(manual = false) {
      if (this.playPending || !this.video.paused) return;
      if (!manual && (this.userPaused || !this.wanted)) return;
      this.playPending = true;
      this.video.play().then(() => {
        if (!this.isConnected || (!manual && !this.wanted)) this.video.pause();
      }).catch(() => {
        if (this.wanted || manual) this.userPaused = true;
      }).finally(() => { this.playPending = false; this.updateControls(); });
    }

    updateControls() {
      const label = this.video.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
      this.toggleText.textContent = label;
      this.toggle.dataset.state = this.video.paused ? 'play' : 'pause';
      this.toggle.setAttribute('aria-label', label);
      this.soundText.textContent = this.video.muted ? this.dataset.soundOnLabel : this.dataset.soundOffLabel;
      this.sound.dataset.state = this.video.muted ? 'muted' : 'audible';
      this.sound.setAttribute('aria-pressed', String(!this.video.muted));
    }

    updateControlsPosition(stageBounds) {
      let bottom = 0;
      if (this.mobile && this.classList.contains('macfox-scroll-video--product')) {
        const sticky = document.querySelector('.mfp__sticky:not([hidden]), .cs-sticky-atc[data-visible="true"]');
        if (sticky) {
          const bar = sticky.getBoundingClientRect();
          if (bar.width > 0 && bar.height > 0 && bar.top < window.innerHeight && bar.bottom > 0) {
            const stage = stageBounds || this.stage.getBoundingClientRect();
            if (stage.bottom > bar.top && stage.top < bar.bottom && stage.right > bar.left && stage.left < bar.right) bottom = Math.ceil(stage.bottom - bar.top + 12);
          }
        }
      }
      if (bottom === this.controlsBottom) return;
      this.controlsBottom = bottom;
      if (bottom) this.style.setProperty('--msv-controls-bottom', `${bottom}px`);
      else this.style.removeProperty('--msv-controls-bottom');
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.mediaVersion += 1;
      this.video?.pause();
      this.light?.remove();
      if (this.frameURL) URL.revokeObjectURL(this.frameURL);
    }
  }
  customElements.define('macfox-scroll-video', MacfoxScrollVideo);
})();
